import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  Storefront,
  PencilSimple,
  Trash,
  X,
  FloppyDisk,
  CheckCircle,
  PauseCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const OPTIONS = {
  product_categories: [
    ['casas', 'Casas'],
    ['apartamentos', 'Apartamentos'],
    ['lotes', 'Lotes'],
    ['salas_comerciais', 'Salas comerciais'],
    ['lojas_comerciais', 'Lojas comerciais'],
    ['bairros_planejados', 'Bairros planejados'],
  ],
  typologies: [
    ['studio', 'Studio'],
    ['1_quarto', '1 quarto'],
    ['2_quartos', '2 quartos'],
    ['3_quartos', '3 quartos'],
    ['4_ou_mais_quartos', '4 ou mais quartos'],
  ],
  purchase_modalities: [
    ['financiamento_bancario', 'Financiamento bancário'],
    ['mcmv', 'Minha Casa Minha Vida'],
    ['vca_facilita', 'VCA Facilita'],
    ['a_vista', 'Pagamento à vista'],
  ],
  purposes: [
    ['moradia', 'Moradia'],
    ['investimento', 'Investimento'],
  ],
};

const EMPTY_FORM = {
  organization_id: 'default',
  state: '',
  city: '',
  product_categories: [],
  typologies: [],
  purchase_modalities: [],
  purposes: [],
  strategic_notes: '',
  active: true,
};

const labelFor = (group, value) => {
  const match = OPTIONS[group].find(
    ([key]) => key === value
  );

  return match ? match[1] : value;
};

const PortfoliosPage = () => {
  const [rows, setRows] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingCities, setLoadingCities] =
    useState(false);
  const [saving, setSaving] = useState(false);

  const fetchRows = async () => {
    try {
      const { data } = await axios.get(
        `${API}/city-portfolios`,
        {
          withCredentials: true,
        }
      );

      setRows(data || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Erro ao carregar portfólios'
      );
    }
  };

  const fetchStates = async () => {
    try {
      const { data } = await axios.get(
        `${API}/locations/states`,
        {
          withCredentials: true,
        }
      );

      setStates(data || []);
    } catch (error) {
      toast.error('Erro ao carregar estados');
    }
  };

  const fetchCities = async (uf) => {
    if (!uf) {
      setCities([]);
      return;
    }

    setLoadingCities(true);

    try {
      const { data } = await axios.get(
        `${API}/locations/states/${uf}/cities`,
        {
          withCredentials: true,
        }
      );

      setCities(data || []);
    } catch (error) {
      setCities([]);

      toast.error(
        error?.response?.data?.detail ||
          'Erro ao carregar cidades'
      );
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        fetchRows(),
        fetchStates(),
      ]);

      setLoading(false);
    };

    init();
  }, []);

  const reset = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setCities([]);
  };

  const changeState = async (uf) => {
    setForm((current) => ({
      ...current,
      state: uf,
      city: '',
    }));

    await fetchCities(uf);
  };

  const toggleValue = (group, value) => {
    setForm((current) => {
      const selected = current[group] || [];

      return {
        ...current,
        [group]: selected.includes(value)
          ? selected.filter(
              (item) => item !== value
            )
          : [...selected, value],
      };
    });
  };

  const edit = async (item) => {
    setEditingId(item.id);

    setForm({
      organization_id:
        item.organization_id || 'default',
      state: item.state || '',
      city: item.city || '',
      product_categories:
        item.product_categories || [],
      typologies: item.typologies || [],
      purchase_modalities:
        item.purchase_modalities || [],
      purposes: item.purposes || [],
      strategic_notes:
        item.strategic_notes || '',
      active: item.active !== false,
    });

    await fetchCities(item.state);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!form.state) {
      toast.error('Selecione o estado');
      return;
    }

    if (!form.city) {
      toast.error('Selecione a cidade');
      return;
    }

    if (
      form.product_categories.length === 0
    ) {
      toast.error(
        'Selecione ao menos uma categoria de produto'
      );
      return;
    }

    setSaving(true);

    try {
      if (editingId) {
        await axios.patch(
          `${API}/city-portfolios/${editingId}`,
          form,
          {
            withCredentials: true,
          }
        );

        toast.success(
          'Portfólio atualizado com sucesso'
        );
      } else {
        await axios.post(
          `${API}/city-portfolios`,
          form,
          {
            withCredentials: true,
          }
        );

        toast.success(
          'Portfólio cadastrado com sucesso'
        );
      }

      reset();
      await fetchRows();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Erro ao salvar portfólio'
      );
    } finally {
      setSaving(false);
    }
  };

  const archive = async (item) => {
    if (
      !window.confirm(
        `Remover o portfólio de ${item.city}/${item.state}?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API}/city-portfolios/${item.id}`,
        {
          withCredentials: true,
        }
      );

      toast.success('Portfólio removido');
      await fetchRows();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Erro ao remover portfólio'
      );
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <header className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-sky-100 text-sky-700">
            <Storefront
              size={30}
              weight="duotone"
            />
          </div>

          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Portfólio Comercial
            </h1>

            <p className="text-slate-600 mt-1">
              Resumo da oferta comercial disponível
              em cada cidade.
            </p>
          </div>
        </header>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {editingId
                  ? 'Editar portfólio'
                  : 'Novo portfólio'}
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                Cadastre o resumo geral da cidade.
                Empreendimentos específicos continuam
                sendo cadastrados separadamente.
              </p>
            </div>

            {editingId && (
              <button
                type="button"
                onClick={reset}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={22} />
              </button>
            )}
          </div>

          <div className="p-6 space-y-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Estado *">
                <select
                  value={form.state}
                  onChange={(event) =>
                    changeState(event.target.value)
                  }
                  className="input-default"
                >
                  <option value="">
                    Selecione o estado
                  </option>

                  {states.map((item) => (
                    <option
                      key={item.uf}
                      value={item.uf}
                    >
                      {item.name} - {item.uf}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Cidade *">
                <select
                  value={form.city}
                  disabled={
                    !form.state ||
                    loadingCities
                  }
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      city: event.target.value,
                    }))
                  }
                  className="input-default disabled:bg-slate-100"
                >
                  <option value="">
                    {loadingCities
                      ? 'Carregando cidades...'
                      : form.state
                        ? 'Selecione a cidade'
                        : 'Selecione o estado primeiro'}
                  </option>

                  {cities.map((item) => (
                    <option
                      key={item.id}
                      value={item.name}
                    >
                      {item.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <OptionGroup
              title="Categorias de produto *"
              options={OPTIONS.product_categories}
              selected={form.product_categories}
              onToggle={(value) =>
                toggleValue(
                  'product_categories',
                  value
                )
              }
            />

            <OptionGroup
              title="Tipologias"
              options={OPTIONS.typologies}
              selected={form.typologies}
              onToggle={(value) =>
                toggleValue(
                  'typologies',
                  value
                )
              }
            />

            <OptionGroup
              title="Modalidades de aquisição"
              options={OPTIONS.purchase_modalities}
              selected={form.purchase_modalities}
              onToggle={(value) =>
                toggleValue(
                  'purchase_modalities',
                  value
                )
              }
            />

            <OptionGroup
              title="Finalidades"
              options={OPTIONS.purposes}
              selected={form.purposes}
              onToggle={(value) =>
                toggleValue(
                  'purposes',
                  value
                )
              }
            />

            <Field
              label="Observações estratégicas"
              helper="Opcional. Não informe preços, estoques ou nomes de empreendimentos que possam mudar frequentemente."
            >
              <textarea
                value={form.strategic_notes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    strategic_notes:
                      event.target.value,
                  }))
                }
                className="input-default min-h-[120px]"
                placeholder="Informações gerais que ajudem a contextualizar o mercado."
              />
            </Field>

            <label className="flex items-center justify-between gap-5 p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div>
                <p className="font-semibold text-slate-900">
                  Disponível para consulta da IA
                </p>

                <p className="text-sm text-slate-500 mt-1">
                  Desative caso o portfólio não deva
                  ser apresentado temporariamente.
                </p>
              </div>

              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    active: event.target.checked,
                  }))
                }
                className="w-5 h-5"
              />
            </label>
          </div>

          <div className="px-6 py-5 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
            {editingId && (
              <button
                type="button"
                onClick={reset}
                className="px-5 py-3 rounded-xl border border-slate-300 font-semibold"
              >
                Cancelar
              </button>
            )}

            <button
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-sky-600 text-white font-semibold disabled:opacity-50"
            >
              <FloppyDisk size={20} />

              {saving
                ? 'Salvando...'
                : editingId
                  ? 'Salvar alterações'
                  : 'Cadastrar portfólio'}
            </button>
          </div>
        </form>

        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">
              Portfólios cadastrados
            </h2>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500">
              Carregando...
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              Nenhum portfólio cadastrado.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((item) => (
                <div
                  key={item.id}
                  className="p-5 flex flex-col xl:flex-row xl:items-center justify-between gap-5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-bold text-slate-900">
                        {item.city}/{item.state}
                      </h3>

                      {item.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                          <CheckCircle size={14} />
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold">
                          <PauseCircle size={14} />
                          Inativo
                        </span>
                      )}
                    </div>

                    <PortfolioLine
                      title="Produtos"
                      values={item.product_categories}
                      group="product_categories"
                    />

                    <PortfolioLine
                      title="Tipologias"
                      values={item.typologies}
                      group="typologies"
                    />

                    <PortfolioLine
                      title="Modalidades"
                      values={item.purchase_modalities}
                      group="purchase_modalities"
                    />

                    <PortfolioLine
                      title="Finalidades"
                      values={item.purposes}
                      group="purposes"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => edit(item)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 font-semibold"
                    >
                      <PencilSimple size={18} />
                      Editar
                    </button>

                    <button
                      type="button"
                      onClick={() => archive(item)}
                      className="p-2 rounded-lg border border-red-200 text-red-600"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

const Field = ({
  label,
  helper,
  children,
}) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label}
    </label>

    {children}

    {helper && (
      <p className="text-xs text-slate-500 mt-2">
        {helper}
      </p>
    )}
  </div>
);

const OptionGroup = ({
  title,
  options,
  selected,
  onToggle,
}) => (
  <div>
    <p className="text-sm font-semibold text-slate-700 mb-3">
      {title}
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {options.map(([value, label]) => (
        <label
          key={value}
          className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white cursor-pointer"
        >
          <input
            type="checkbox"
            checked={selected.includes(value)}
            onChange={() => onToggle(value)}
            className="w-5 h-5"
          />

          <span className="text-sm font-medium text-slate-700">
            {label}
          </span>
        </label>
      ))}
    </div>
  </div>
);

const PortfolioLine = ({
  title,
  values,
  group,
}) => {
  if (!values || values.length === 0) {
    return null;
  }

  return (
    <p className="text-sm text-slate-600 mt-2">
      <strong>{title}:</strong>{' '}
      {values
        .map((value) =>
          labelFor(group, value)
        )
        .join(', ')}
    </p>
  );
};

export default PortfoliosPage;
